import { logger } from 'packages';
import * as gtm from 'tradera-apps/tradera/components/discover/helpers/search-gtm';
import { getReturnUrl } from 'tradera-utils/login';
import { DEFAULT_COOKIES } from 'tradera-constants/cookies';
import { API_ERRORS } from 'tradera-utils/api';
import { cache, CACHE_KEYS } from 'tradera-utils/cache';
import cookieUtil from 'tradera-utils/cookie';
import actionTypes from './action-types';
import {
    getSystemMessages,
    getMessagingNotifications,
    getDismissSwish
} from './api';
import itemActions from '../items/actions';
import { getItem } from '../items/api';
import getInitialState from './initial-state';

const initialize = initData => ({
    type: actionTypes.INITIALIZE,
    payload: getInitialState(initData)
});

const toggleHeaderSearch = payload => ({
    type: actionTypes.TOGGLE_HEADER_SEARCH,
    payload
});

const openGdprModal = payload => ({
    type: actionTypes.OPEN_GDPR_MODAL,
    payload
});

const closeGdprModal = payload => ({
    type: actionTypes.CLOSE_GDPR_MODAL,
    payload
});

const openGdprSettings = payload => ({
    type: actionTypes.OPEN_GDPR_SETTINGS,
    payload
});

const closeGdprSettings = payload => ({
    type: actionTypes.CLOSE_GDPR_SETTINGS,
    payload
});

const openQuickView = payload => (dispatch, getState) => {
    const { openedFrom, ...restOfPayload } = payload;

    if (getState().ui.quickView.loading) {
        return;
    }
    dispatch({
        type: actionTypes.LOADING_QUICK_VIEW,
        payload: true
    });

    return getItem(restOfPayload.itemId)
        .then(({ data }) => {
            if (data) {
                const updatedData = {
                    ...data,
                    imageFormats: data.imageFormats?.map(i => ({
                        ...i,
                        alt: i.alt || data.shortDescription
                    }))
                };
                dispatch(itemActions.add([updatedData]));
            }
            dispatch({
                type: actionTypes.OPEN_QUICK_VIEW,
                payload: restOfPayload
            });
            gtm.trackAction('Quick View', `Open: from ${openedFrom}`);
            dispatch({
                type: actionTypes.LOADING_QUICK_VIEW,
                payload: false
            });
            return data;
        })
        .catch(error => {
            dispatch(closeQuickView());
            dispatch({
                type: actionTypes.LOADING_QUICK_VIEW,
                payload: false
            });
            logger(error);
        });
};

const closeQuickView = payload => ({
    type: actionTypes.CLOSE_QUICK_VIEW,
    payload
});

const openVideoModal = payload => ({
    type: actionTypes.OPEN_VIDEO_MODAL,
    payload
});

const closeVideoModal = payload => ({
    type: actionTypes.CLOSE_VIDEO_MODAL,
    payload
});

const openYoutubeVideoModal = payload => ({
    type: actionTypes.OPEN_YOUTUBE_VIDEO_MODAL,
    payload
});

const closeYoutubeVideoModal = payload => ({
    type: actionTypes.CLOSE_YOUTUBE_VIDEO_MODAL,
    payload
});

/**
 * signIn
 * @param {object} payload
 * @param {string} payload.redirectUrl - where to send the user after login completes
 */
const signIn = payload => (dispatch, getStore) => {
    const {
        environment: { featureSwitches }
    } = getStore();

    if (!featureSwitches['login-anywhere']) {
        window.location = `/login${getReturnUrl(payload.redirectUrl)}`;
    } else {
        dispatch(openProfileMenu(payload.redirectUrl));
    }
};

/*
    Changes made to messaging modal will affect My Tradera as well!
*/
const openMessagingModal = payload => ({
    type: actionTypes.OPEN_MESSAGING_MODAL,
    payload
});

const closeMessagingModal = payload => ({
    type: actionTypes.CLOSE_MESSAGING_MODAL,
    payload
});

/**
 * Open a notification optin modal
 *
 * @param {boolean} type - type of notifications to show modal for
 * @returns {function(*, *): boolean}
 */
const openNotificationOptinModal = (
    modalType,
    gaLabel,
    title,
    description,
    tagGroups
) => async (dispatch, getState) => {
    const { pushNotifications } = getState();
    if (!pushNotifications.enabled) {
        return;
    }
    if (pushNotifications.shownModals[modalType] === true) {
        return;
    }
    dispatch({
        type: actionTypes.OPEN_NOTIFICATION_OPTIN_MODAL,
        payload: {
            modalType,
            gaLabel,
            title,
            description,
            tagGroups
        }
    });
};

const closeNotificationOptinModal = payload => ({
    type: actionTypes.CLOSE_NOTIFICATION_OPTIN_MODAL,
    payload
});

const openProfileMenu = payload => ({
    type: actionTypes.OPEN_PROFILE_MENU,
    payload
});

const closeProfileMenu = payload => ({
    type: actionTypes.CLOSE_PROFILE_MENU,
    payload
});

const openCategoryDropdownMenu = payload => ({
    type: actionTypes.OPEN_CATEGORY_DROPDOWN_MENU,
    payload
});

const closeCategoryDropdownMenu = payload => ({
    type: actionTypes.CLOSE_CATEGORY_DROPDOWN_MENU,
    payload
});

const openSearchForm = payload => ({
    type: actionTypes.OPEN_SEARCH_FORM,
    payload
});

const closeSearchForm = payload => ({
    type: actionTypes.CLOSE_SEARCH_FORM,
    payload
});

const updateItemLayout = value => {
    const defaultCookie = DEFAULT_COOKIES['ITEM_LAYOUT'];
    const expires = defaultCookie.length;
    try {
        cookieUtil.createCookie(defaultCookie.key, value, expires);
    } catch (e) {
        logger(e);
    }
    return {
        type: actionTypes.ITEM_LAYOUT,
        payload: value
    };
};

const updateSiteLayout = value => {
    const defaultCookie = DEFAULT_COOKIES['SITE_LAYOUT'];
    const expires = defaultCookie.length;
    try {
        cookieUtil.createCookie(defaultCookie.key, value, expires);
    } catch (e) {
        logger(e);
    }
    return {
        type: actionTypes.SITE_LAYOUT,
        payload: value
    };
};

const updateSystemMessages = systemMessages => ({
    type: actionTypes.UPDATE_SYSTEM_MESSAGES,
    payload: systemMessages
});

const fetchSystemMessages = () => async (dispatch, getState) => {
    const {
        environment: { featureSwitches },
        member: { isLoggedIn }
    } = getState();
    if (featureSwitches && featureSwitches['mytradera-alerts'] && isLoggedIn) {
        try {
            let systemMessages = cache.read(CACHE_KEYS.SYSTEM_MESSAGES);
            if (!systemMessages) {
                const fetchedMessages = await getSystemMessages();
                systemMessages = {};
                if (fetchedMessages && 'items' in fetchedMessages.data) {
                    fetchedMessages.data.items.forEach(({ type }) => {
                        systemMessages[type] = true;
                    });
                    cache.write(CACHE_KEYS.SYSTEM_MESSAGES, systemMessages);
                }
            }
            dispatch(updateSystemMessages(systemMessages));
        } catch (error) {
            // Invalidate cache to fetch fresh again next action
            cache.remove(CACHE_KEYS.SYSTEM_MESSAGES);
            switch (error.message) {
                case API_ERRORS.LOGGED_OUT:
                    await dispatch(logOut());
                    window.location = '/login/logout';
                    break;
                case API_ERRORS.IGNORE_ME:
                case API_ERRORS.CANCELLED:
                case API_ERRORS.NETWORK:
                case API_ERRORS.TIMEOUT:
                    break;
                default:
                    logger(error);
                    break;
            }
        }
    }
};

const updateMessagingNotifications = payload => ({
    type: actionTypes.UPDATE_MESSAGING_NOTIFICATIONS,
    payload
});

const fetchMessagingNotifications = () => async dispatch => {
    try {
        let messagingNotifications = cache.read(CACHE_KEYS.MESSAGING_BADGES);
        if (!messagingNotifications) {
            const { data } = await getMessagingNotifications();
            messagingNotifications = data;
        }
        dispatch(updateMessagingNotifications(messagingNotifications));
    } catch (error) {
        // Fail silently
    }
};

const dismissSwish = () => dispatch => {
    dispatch({
        type: actionTypes.UPDATE_SYSTEM_MESSAGES,
        payload: {
            SwishNumberMissing: false
        }
    });
    cache.remove(CACHE_KEYS.SYSTEM_MESSAGES);
    getDismissSwish().catch(() => {
        // Fail silently
    });
};

const logOut = () => async dispatch => {
    cache.remove(CACHE_KEYS.MESSAGING_BADGES);
    cache.remove(CACHE_KEYS.SYSTEM_MESSAGES);
    dispatch({
        type: actionTypes.LOG_OUT,
        payload: {}
    });
};

const openItemSettings = payload => ({
    type: actionTypes.OPEN_ITEM_SETTINGS,
    payload: { item: payload }
});

const closeItemSettings = payload => ({
    type: actionTypes.CLOSE_ITEM_SETTINGS,
    payload
});

export default {
    initialize,
    toggleHeaderSearch,
    openGdprModal,
    closeGdprModal,
    openGdprSettings,
    closeGdprSettings,
    openQuickView,
    closeQuickView,
    openVideoModal,
    closeVideoModal,
    openYoutubeVideoModal,
    closeYoutubeVideoModal,
    signIn,
    openMessagingModal,
    closeMessagingModal,
    openNotificationOptinModal,
    closeNotificationOptinModal,
    updateItemLayout,
    updateSiteLayout,
    updateSystemMessages,
    fetchSystemMessages,
    updateMessagingNotifications,
    fetchMessagingNotifications,
    dismissSwish,
    openProfileMenu,
    closeProfileMenu,
    openCategoryDropdownMenu,
    closeCategoryDropdownMenu,
    openSearchForm,
    closeSearchForm,
    openItemSettings,
    closeItemSettings,
    logOut
};
